import { useMutation, useQuery } from '@tanstack/react-query'
import { consoleApi, getApiErrorMessage, consoleQueries } from '@milliswift/api-client'
import {
  Badge,
  Button,
  Callout,
  Modal,
  PageSkeleton,
  PreviewDataBadge,
  SectionHeader,
  Stepper,
  Timeline,
  TypedConfirmDialog,
  useToast,
} from '@milliswift/ui'
import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import type { SnapshotCommit } from '@milliswift/types'
import { useDb } from '../lib/db'

/**
 * Snapshot history and restore.
 *
 * Snapshots chain like commits: each has a short hash, a size delta versus its
 * parent, and the tables it covers. Restoring replaces the current state, so
 * it is gated behind typing the hash — a misclick must not be enough.
 */
export default function Snapshots() {
  const { databaseId } = useDb()
  const toast = useToast()

  const snapshots = useQuery({
    ...consoleQueries.snapshots(databaseId ?? ''),
    enabled: Boolean(databaseId),
  })

  const [restoreTarget, setRestoreTarget] = useState<SnapshotCommit | null>(null)
  const [restoring, setRestoring] = useState<SnapshotCommit | null>(null)

  const startRestore = useMutation({
    mutationFn: (commit: SnapshotCommit) =>
      consoleApi.restoreSnapshot(databaseId ?? '', commit.commit_hash),
    onSuccess: (_data, commit) => {
      setRestoreTarget(null)
      setRestoring(commit)
      toast.success('Restore started', `${commit.commit_hash} — queries are paused while it runs.`)
    },
    onError: (error) => toast.error('Could not start the restore', getApiErrorMessage(error)),
  })

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Snapshots"
        description="Daily commits of your database. Restore to any point in the retention window."
        action={<PreviewDataBadge />}
      />

      {!databaseId ? (
        <Callout tone="info">Select a database in the header first.</Callout>
      ) : snapshots.isPending ? (
        <PageSkeleton />
      ) : snapshots.isError ? (
        <Callout tone="danger" title="Could not load snapshots">
          {getApiErrorMessage(snapshots.error)}
        </Callout>
      ) : (
        <div className="rounded-card border border-line bg-surface p-6 shadow-card">
          <Timeline
            entries={(snapshots.data ?? []).map((commit) => ({
              id: commit.commit_hash,
              title: (
                <span className="font-mono">
                  {commit.commit_hash}
                  <Badge size="sm" tone="neutral" className="ml-2">
                    +{(commit.size_delta_bytes / 1e9).toFixed(1)} GB
                  </Badge>
                </span>
              ),
              description: `${commit.table_count} tables`,
              timestamp: new Date(commit.created_at).toLocaleString(),
              tone: 'success' as const,
              detail: (
                <div className="space-y-2">
                  <ul className="space-y-1">
                    {commit.tables.map((table) => (
                      <li
                        key={table.table_name}
                        className="flex justify-between gap-4 font-mono text-caption"
                      >
                        <span className="text-foreground">{table.table_name}</span>
                        <span className="text-muted">
                          {(table.size_bytes / 1e9).toFixed(1)} GB
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRestoreTarget(commit)}
                  >
                    Restore to this point
                  </Button>
                </div>
              ),
            }))}
          />
        </div>
      )}

      {/* Confirm: type the hash */}
      <TypedConfirmDialog
        open={restoreTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRestoreTarget(null)
        }}
        title={`Restore to ${restoreTarget?.commit_hash ?? ''}?`}
        description="The current state is replaced. Everything written after this snapshot is lost."
        confirmationText={restoreTarget?.commit_hash ?? ''}
        confirmLabel="Restore"
        loading={startRestore.isPending}
        onConfirm={() => restoreTarget && startRestore.mutate(restoreTarget)}
      >
        <Callout tone="warning" icon={<AlertTriangle className="h-4 w-4" />}>
          Queries are paused for the duration — usually a few minutes.
        </Callout>
      </TypedConfirmDialog>

      {/* Progress */}
      <Modal
        open={restoring !== null}
        onOpenChange={() => setRestoring(null)}
        title={`Restoring to ${restoring?.commit_hash ?? ''}`}
        dismissible={false}
      >
        <div className="space-y-5">
          <Stepper
            current={1}
            steps={[
              { label: 'Pause query engine', description: 'done' },
              { label: 'Restore snapshot', description: 'in progress' },
              { label: 'Resume query engine' },
              { label: 'Validate integrity' },
            ]}
          />
          <Callout tone="info">
            This page can be closed — the restore continues on the server.
          </Callout>
        </div>
      </Modal>
    </div>
  )
}
